interface Sample {
  time: number;
  bytes: number;
}

const WINDOW_MS = 5000;
const SMOOTHING_ALPHA = 0.25;

/**
 * Tracks cumulative uploaded bytes over time and derives a jitter-smoothed
 * throughput figure plus an ETA. Instantaneous per-chunk speed is noisy
 * (network hiccups, part boundaries), so this keeps a short rolling window
 * of samples and exponentially smooths the result frame to frame.
 */
export class SpeedTracker {
  private samples: Sample[] = [];
  private smoothedBytesPerSecond = 0;

  update(uploadedBytes: number): void {
    const now = performance.now();
    this.samples.push({ time: now, bytes: uploadedBytes });
    while (this.samples.length > 1 && now - this.samples[0]!.time > WINDOW_MS) {
      this.samples.shift();
    }

    if (this.samples.length < 2) return;

    const first = this.samples[0]!;
    const last = this.samples[this.samples.length - 1]!;
    const dt = (last.time - first.time) / 1000;
    if (dt <= 0) return;

    const instantSpeed = (last.bytes - first.bytes) / dt;
    this.smoothedBytesPerSecond =
      this.smoothedBytesPerSecond === 0
        ? instantSpeed
        : SMOOTHING_ALPHA * instantSpeed + (1 - SMOOTHING_ALPHA) * this.smoothedBytesPerSecond;
  }

  reset(): void {
    this.samples = [];
    this.smoothedBytesPerSecond = 0;
  }

  getSpeedBytesPerSecond(): number {
    return Math.max(0, this.smoothedBytesPerSecond);
  }

  getEtaSeconds(remainingBytes: number): number | null {
    if (this.smoothedBytesPerSecond <= 1) return null;
    return remainingBytes / this.smoothedBytesPerSecond;
  }
}
